import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BackToCockpit from "@/components/BackToCockpit";
import InstantQuoteWidget from "@/components/InstantQuoteWidget";
import {
  Plane, Calendar as CalIcon, List, Sparkles, MapPin, Users, ShieldCheck,
  Building2, Clock, ArrowRight, Loader2, Radar, Send, DollarSign, TrendingUp,
} from "lucide-react";

// ---- helpers ---------------------------------------------------------------
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function fmtUSD(n) { return "$" + (n || 0).toLocaleString(); }
function isoDate(d) { return d.toISOString().slice(0, 10); }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

// ---- filter tabs -----------------------------------------------------------
const TABS = [
  { key: "home", label: "My Home Base", icon: MapPin },
  { key: "region", label: "By Region", icon: Radar },
  { key: "all", label: "All Legs", icon: Plane },
];
const REGIONS = ["US-East", "US-West", "Caribbean", "Europe", "Mexico"];

export default function RiderDashboard() {
  const [dash, setDash] = useState(null);
  const [slots, setSlots] = useState([]);
  const [tab, setTab] = useState("all");
  const [region, setRegion] = useState("US-East");
  const [view, setView] = useState("calendar"); // "calendar" | "list"
  const [monthDate, setMonthDate] = useState(startOfMonth(new Date()));
  const [busy, setBusy] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reservingSeats, setReservingSeats] = useState(1);
  const [reservations, setReservations] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [d, res] = await Promise.all([
          api.get("/rider/dashboard"),
          api.get("/empty-leg/reservations/mine"),
        ]);
        setDash(d.data);
        setReservations(res.data.reservations || []);
      } catch (e) {
        if (e?.response?.status === 403) {
          toast.error("Your rider membership isn't active yet.");
        }
      }
    })();
  }, []);

  // Load slots whenever filter changes
  useEffect(() => {
    setBusy(true);
    const params = {};
    if (tab === "home" && dash?.membership?.home_airport_icao) {
      params.home_base = dash.membership.home_airport_icao;
    } else if (tab === "region") {
      params.region = region;
    }
    api.get("/empty-leg/slots", { params })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setBusy(false));
  }, [tab, region, dash?.membership?.home_airport_icao]);

  // Group slots by ISO date for calendar dots
  const slotsByDate = useMemo(() => {
    const m = {};
    for (const s of slots) {
      (m[s.departure_date] ||= []).push(s);
    }
    return m;
  }, [slots]);

  // Reservation
  const reserve = async () => {
    if (!selectedSlot) return;
    try {
      const r = await api.post(`/empty-leg/slots/${selectedSlot.id}/reserve`, null, {
        params: { seats: reservingSeats },
      });
      toast.success(`Reservation request sent to ${selectedSlot.operator_name}. ${reservingSeats} seat(s) held.`);
      setSelectedSlot(null);
      setReservingSeats(1);
      const res = await api.get("/empty-leg/reservations/mine");
      setReservations(res.data.reservations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reserve — try again.");
    }
  };

  if (!dash) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan" />
      </div>
    );
  }

  const home = dash.membership.home_airport_icao;
  const tsaPct = dash.tsa.completion_pct || 0;

  return (
    <div className="max-w-7xl mx-auto px-6 pt-8 pb-24" data-testid="rider-dashboard">
      <BackToCockpit />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-5">
        <div
          className="rounded-2xl border border-cyan/30 p-6 relative overflow-hidden"
          style={{ background: "linear-gradient(140deg, rgba(75,156,255,0.12) 0%, rgba(5,8,16,0.9) 60%)" }}
        >
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// JUMPSEAT · RIDER COCKPIT</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2 leading-[0.95]">
            Welcome aboard.
          </h1>
          <p className="text-slate-300 mt-3 max-w-lg">
            Every empty ferry that could carry you — surfaced first, priced right, and one click to reserve.
            When nothing fits, spin a full charter quote below.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/rider/tsa" data-testid="rider-tsa-cta" className="btn-hud btn-hud-solid">
              <ShieldCheck className="h-3.5 w-3.5" /> {tsaPct >= 100 ? "TSA PROFILE COMPLETE" : `COMPLETE TSA PROFILE · ${tsaPct}%`}
            </Link>
            <button
              type="button"
              onClick={() => setShowRequestModal(true)}
              data-testid="rider-post-request-cta"
              className="btn-hud"
            >
              <Send className="h-3.5 w-3.5" /> POST A LEG REQUEST
            </button>
            <a href="#quote" className="btn-hud">
              <Sparkles className="h-3.5 w-3.5" /> INSTANT CHARTER QUOTE
            </a>
          </div>
        </div>

        {/* Membership stats */}
        <div
          className="rounded-2xl border border-white/10 p-6"
          style={{ background: "linear-gradient(160deg, rgba(46,213,115,0.08) 0%, rgba(5,8,16,0.85) 60%)" }}
        >
          <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: "#5FE895" }}>
            // MEMBERSHIP · {dash.membership.status?.toUpperCase() || "PENDING"}
          </div>
          <div className="mt-2 font-display text-xl font-semibold text-white">
            {dash.membership.plan === "crew_free" ? "Crew Perk · Free Access" : "Annual Rider · $10K/yr"}
          </div>
          <div className="text-[11px] text-white/50 mono mt-1">
            Home base · {home || "Set your ICAO on profile"}
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <Stat icon={CalIcon} value={dash.stats.upcoming_reservations} label="Upcoming" />
            <Stat icon={Plane} value={dash.stats.completed_flights} label="Completed" />
            <Stat icon={DollarSign} value={"~" + fmtUSD(dash.stats.estimated_savings_usd)} label="Est. savings" />
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] text-white/60 mono">
              <span>TSA profile</span><span>{tsaPct}%</span>
            </div>
            <div className="h-2 mt-1 rounded-full bg-white/[0.06] overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${tsaPct}%`, background: tsaPct >= 100 ? "#2ED573" : "#4B9CFF" }} />
            </div>
          </div>
        </div>
      </div>

      {/* ── CALENDAR + LIST TABS ──────────────────────────────────────── */}
      <div className="mt-12 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// AVAILABLE JUMPSEATS</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            {slots.length} {slots.length === 1 ? "JumpSeat" : "JumpSeats"} available to reserve
          </h2>
        </div>
        <div className="flex gap-1.5 rounded-full border border-white/10 p-1 bg-black/40">
          <ViewBtn active={view === "calendar"} onClick={() => setView("calendar")} icon={CalIcon} label="Calendar" tid="rider-view-cal" />
          <ViewBtn active={view === "list"}     onClick={() => setView("list")}     icon={List}    label="List"     tid="rider-view-list" />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`rider-tab-${t.key}`}
              className={`mono text-[10.5px] tracking-widest uppercase px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                on ? "border-cyan/70 bg-cyan/15 text-cyan" : "border-white/10 text-white/60 hover:bg-white/5"
              }`}
            >
              <Icon className="h-3 w-3" /> {t.label}
              {t.key === "home" && home && <span className="ml-1 opacity-60">· {home}</span>}
            </button>
          );
        })}

        {tab === "region" && (
          <div className="flex gap-1.5 flex-wrap ml-2">
            {REGIONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRegion(r)}
                data-testid={`rider-region-${r}`}
                className={`mono text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border ${
                  region === r ? "border-cyan/60 bg-cyan/10 text-cyan" : "border-white/10 text-white/50 hover:bg-white/5"
                }`}
              >{r}</button>
            ))}
          </div>
        )}

        {busy && <Loader2 className="h-3.5 w-3.5 text-cyan animate-spin ml-2" />}
      </div>

      {/* Calendar view */}
      {view === "calendar" && (
        <MonthCalendar
          monthDate={monthDate}
          onNav={setMonthDate}
          slotsByDate={slotsByDate}
          onPickSlot={setSelectedSlot}
        />
      )}

      {/* List view */}
      {view === "list" && (
        <div className="mt-6 grid gap-2" data-testid="rider-slot-list">
          {slots.length === 0 && (
            <div className="text-white/50 mono text-[12px] text-center py-10 border border-white/10 rounded-xl">
              No legs match this filter. Try another tab or post a leg request.
            </div>
          )}
          {slots.map((s) => (
            <SlotRow key={s.id} s={s} onPick={() => setSelectedSlot(s)} />
          ))}
        </div>
      )}

      {/* ── MY UPCOMING RESERVATIONS ──────────────────────────────────── */}
      {reservations.length > 0 && (
        <section className="mt-14">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// MY RESERVATIONS</div>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1 mb-4">Upcoming + past bookings</h3>
          <div className="grid gap-2" data-testid="rider-reservations">
            {reservations.map((r) => (
              <div key={r.reservation.id} className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 flex items-center gap-4 flex-wrap">
                <div className="mono text-[11px] tracking-widest text-cyan">{r.departure_date}</div>
                <div className="font-display font-semibold text-white">
                  {r.origin_icao} <ArrowRight className="inline h-3.5 w-3.5 opacity-60" /> {r.destination_icao}
                </div>
                <div className="text-[12px] text-white/60">{r.aircraft_type} · {r.operator_name}</div>
                <div className="ml-auto flex items-center gap-2">
                  <span className={`mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${
                    r.reservation.status === "confirmed"
                      ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                      : "border-amber-400/40 bg-amber-400/10 text-amber-300"
                  }`}>{r.reservation.status}</span>
                  <span className="mono text-[11px] text-white/70">× {r.reservation.seats} seat{r.reservation.seats > 1 ? "s" : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── INSTANT CHARTER QUOTE (embedded) ──────────────────────────── */}
      <section id="quote" className="mt-16 scroll-mt-24">
        <div className="rounded-2xl border border-cyan/20 p-6 md:p-8 bg-black/40 backdrop-blur">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// NO JUMPSEAT MATCHING? · GO DIRECT</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Instant charter quote</h2>
              <p className="text-slate-300 mt-2 max-w-2xl text-[13.5px]">
                Multi-leg trip builder with 3D globe visualization, live wind + tankering math,
                and real-time bids from our verified operator network. Same engine as our
                dedicated <Link to="/quote" className="underline text-cyan">Instant PJ Quote page</Link>.
              </p>
            </div>
          </div>
          <InstantQuoteWidget />
        </div>
      </section>

      {/* ── MODALS ────────────────────────────────────────────────────── */}
      {selectedSlot && (
        <ReserveModal
          slot={selectedSlot}
          seats={reservingSeats}
          setSeats={setReservingSeats}
          onClose={() => setSelectedSlot(null)}
          onConfirm={reserve}
        />
      )}
      {showRequestModal && (
        <PostRequestModal
          home={home}
          onClose={() => setShowRequestModal(false)}
        />
      )}
    </div>
  );
}

// ── UI PARTS ────────────────────────────────────────────────────────────────
function Stat({ icon: Icon, value, label }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/40 p-2.5">
      <div className="flex items-center gap-1 text-cyan/70"><Icon className="h-3 w-3" /></div>
      <div className="font-display text-lg font-bold text-white mt-0.5 leading-none">{value}</div>
      <div className="mono text-[9px] tracking-widest uppercase text-white/50 mt-1">{label}</div>
    </div>
  );
}

function ViewBtn({ active, onClick, icon: Icon, label, tid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={tid}
      className={`mono text-[10.5px] tracking-widest uppercase px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${
        active ? "bg-cyan text-obsidian" : "text-white/60 hover:text-white"
      }`}
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function MonthCalendar({ monthDate, onNav, slotsByDate, onPickSlot }) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const first = new Date(y, m, 1);
  const lead = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-black/40 p-4" data-testid="rider-calendar">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => onNav(new Date(y, m - 1, 1))}
          data-testid="rider-cal-prev"
          className="mono text-[10px] tracking-widest uppercase px-3 py-1 rounded border border-white/10 text-white/60 hover:bg-white/5"
        >← Prev</button>
        <div className="font-display text-lg font-semibold text-white">{MONTHS[m]} {y}</div>
        <button
          type="button"
          onClick={() => onNav(new Date(y, m + 1, 1))}
          data-testid="rider-cal-next"
          className="mono text-[10px] tracking-widest uppercase px-3 py-1 rounded border border-white/10 text-white/60 hover:bg-white/5"
        >Next →</button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="mono text-[9px] tracking-widest uppercase text-white/40 text-center py-1">{w}</div>
        ))}
        {cells.map((d, i) => {
          const key = d ? isoDate(d) : `pad-${i}`;
          const dayLegs = d ? (slotsByDate[isoDate(d)] || []) : [];
          const today = d && isoDate(d) === isoDate(new Date());
          return (
            <div
              key={key}
              className={`min-h-[76px] p-1.5 rounded-md border ${d ? "border-white/[0.08] bg-white/[0.02]" : "border-transparent"} ${today ? "ring-1 ring-cyan/60" : ""}`}
              data-testid={d ? `rider-cal-cell-${key}` : undefined}
            >
              {d && (
                <>
                  <div className={`mono text-[10px] tracking-widest ${today ? "text-cyan font-bold" : "text-white/50"}`}>{d.getDate()}</div>
                  <div className="mt-1 space-y-0.5">
                    {dayLegs.slice(0, 3).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => onPickSlot(s)}
                        data-testid={`rider-cal-leg-${s.id}`}
                        className="w-full text-left mono text-[9.5px] tracking-tighter px-1.5 py-0.5 rounded bg-cyan/15 border border-cyan/30 text-cyan hover:bg-cyan/25 truncate"
                      >
                        {s.origin_icao}→{s.destination_icao}
                      </button>
                    ))}
                    {dayLegs.length > 3 && (
                      <div className="mono text-[9px] text-white/40">+{dayLegs.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlotRow({ s, onPick }) {
  const seatsLeft = (s.seats_available || 0) - ((s.reservations || []).reduce((t, r) => t + (r.seats || 1), 0));
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={`rider-slot-row-${s.id}`}
      className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] px-4 py-3 flex items-center gap-4 flex-wrap transition-colors"
    >
      <div className="mono text-[11px] tracking-widest text-cyan">
        {s.departure_date}{s.departure_time_local && <> · {s.departure_time_local}</>}
      </div>
      <div className="font-display font-semibold text-white">
        {s.origin_icao} <ArrowRight className="inline h-3.5 w-3.5 opacity-60" /> {s.destination_icao}
      </div>
      <div className="text-[12px] text-white/60">{s.aircraft_type} · {s.operator_name}</div>
      <div className="ml-auto flex items-center gap-3">
        <span className="mono text-[10px] tracking-widest text-white/60">
          <Users className="inline h-3 w-3 mr-0.5" />{seatsLeft} / {s.seats_available} seats
        </span>
        <span className="font-display font-bold text-cyan">{fmtUSD(s.seat_price_usd)} <span className="text-[10px] text-white/50 font-normal">/ seat</span></span>
      </div>
    </button>
  );
}

function ReserveModal({ slot, seats, setSeats, onClose, onConfirm }) {
  const seatsLeft = (slot.seats_available || 0) - ((slot.reservations || []).reduce((t, r) => t + (r.seats || 1), 0));
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="max-w-md w-full rounded-2xl border border-cyan/30 bg-obsidian p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="rider-reserve-modal"
      >
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// RESERVE JUMPSEAT</div>
        <div className="font-display text-2xl font-bold text-white mt-2">
          {slot.origin_icao} → {slot.destination_icao}
        </div>
        <div className="text-[12px] text-white/70 mt-1">
          {slot.departure_date}{slot.departure_time_local && ` · ${slot.departure_time_local}`}
          {" · "}{slot.aircraft_type}
          {" · "}<span className="text-cyan">{slot.operator_name}</span>
        </div>
        {slot.empty_leg_notes && (
          <div className="mt-3 text-[12px] text-white/60 italic">{slot.empty_leg_notes}</div>
        )}
        <div className="mt-5">
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-2">SEATS · {seatsLeft} AVAILABLE</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSeats(Math.max(1, seats - 1))} className="mono w-8 h-8 rounded border border-white/15 text-white/70">−</button>
            <div data-testid="rider-reserve-seat-count" className="font-display text-2xl font-bold text-white w-10 text-center">{seats}</div>
            <button type="button" onClick={() => setSeats(Math.min(seatsLeft, seats + 1))} className="mono w-8 h-8 rounded border border-white/15 text-white/70">+</button>
            <div className="ml-auto font-display text-xl font-bold text-cyan">{fmtUSD(seats * (slot.seat_price_usd || 0))}</div>
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-hud">Cancel</button>
          <button type="button" onClick={onConfirm} data-testid="rider-reserve-confirm" className="btn-hud btn-hud-solid">
            <Send className="h-3.5 w-3.5" /> REQUEST RESERVATION
          </button>
        </div>
      </div>
    </div>
  );
}

function PostRequestModal({ home, onClose }) {
  const [f, setF] = useState({
    origin_icao: home || "",
    destination_icao: "",
    earliest_date: isoDate(new Date()),
    latest_date: isoDate(new Date(Date.now() + 30 * 86400000)),
    pax_count: 1,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.origin_icao || !f.destination_icao) {
      toast.error("Origin and destination are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/empty-leg/rider-requests", f);
      toast.success("Leg request posted. Operators will surface matching ferries.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post request.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="max-w-lg w-full rounded-2xl border border-cyan/30 bg-obsidian p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="rider-request-modal"
      >
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// POST A LEG REQUEST</div>
        <div className="font-display text-2xl font-bold text-white mt-2">Where do you want to go?</div>
        <p className="text-[12px] text-white/60 mt-1">
          Operators see your request and stage empty legs that match — you get first look before it hits the public feed.
        </p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Field label="Origin ICAO" value={f.origin_icao} onChange={(v) => setF({ ...f, origin_icao: v.toUpperCase() })} testid="rider-req-orig" />
          <Field label="Destination ICAO" value={f.destination_icao} onChange={(v) => setF({ ...f, destination_icao: v.toUpperCase() })} testid="rider-req-dest" />
          <Field label="Earliest date" type="date" value={f.earliest_date} onChange={(v) => setF({ ...f, earliest_date: v })} testid="rider-req-earliest" />
          <Field label="Latest date" type="date" value={f.latest_date} onChange={(v) => setF({ ...f, latest_date: v })} testid="rider-req-latest" />
          <Field label="Passengers" type="number" value={f.pax_count} onChange={(v) => setF({ ...f, pax_count: parseInt(v || "1", 10) })} testid="rider-req-pax" />
        </div>
        <div className="mt-3">
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Notes (optional)</div>
          <textarea
            rows={3}
            value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
            className="input w-full"
            placeholder="Flexibility, catering, pets, +1s…"
            data-testid="rider-req-notes"
          />
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-hud">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="rider-req-submit" className="btn-hud btn-hud-solid">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> POST REQUEST</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input w-full"
        data-testid={testid}
      />
    </div>
  );
}
